// App Store Guideline 5.1.2 Compliance: Clear data collection notice
// Users are informed about what data is collected during sign-up

import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/hooks/useTheme";
import fitlinkLogo from "@/assets/fitlink-logo-new.png";
import { z } from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ComprehensiveOnboarding } from "@/components/ComprehensiveOnboarding";
import { Shield } from "lucide-react";

const passwordSchema = z.string()
  .min(12, 'Password must be at least 12 characters')
  .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Must contain at least one special character');

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingUserId, setOnboardingUserId] = useState<string>("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate("/dashboard");
      }
    });

    // Handle auth callbacks (email verification and password reset)
    const handleAuthCallback = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      
      if (type === 'recovery') {
        // User clicked password reset link
        setIsResettingPassword(true);
        toast({
          title: "Set New Password",
          description: "Please enter your new password below.",
        });
      } else if (type === 'signup' || type === 'magiclink') {
        // User clicked email verification link - auto verify
        const { error } = await supabase.auth.getSession();
        if (!error) {
          toast({
            title: "Email Verified! ✅",
            description: "Your email has been verified successfully.",
          });
          // Redirect to dashboard if logged in
          setTimeout(() => {
            supabase.auth.getSession().then(({ data: { session } }) => {
              if (session) {
                navigate("/dashboard");
              }
            });
          }, 1500);
        }
      }
    };

    handleAuthCallback();
  }, [navigate, toast]);

  const handleUpdatePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: "Passwords Don't Match",
        description: "Please make sure both passwords match.",
      });
      return;
    }

    // Validate password strength
    const passwordValidation = passwordSchema.safeParse(newPassword);
    if (!passwordValidation.success) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: "Password Requirements Not Met",
        description: passwordValidation.error.errors[0].message,
      });
      return;
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    setLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } else {
      toast({
        title: "Password Updated! ✅",
        description: "Your password has been successfully reset.",
      });
      setIsResettingPassword(false);
      setNewPassword("");
      setConfirmPassword("");
      // Redirect to dashboard
      setTimeout(() => navigate("/dashboard"), 1500);
    }
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("signup-email") as string;
    const password = formData.get("signup-password") as string;
    const username = formData.get("username") as string;

    // Validate password strength
    const passwordValidation = passwordSchema.safeParse(password);
    if (!passwordValidation.success) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: "Password Requirements Not Met",
        description: passwordValidation.error.errors[0].message,
      });
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: {
          username,
        },
      },
    });

    setLoading(false);

    if (error) {
      // Check if it's a unique constraint violation for username
      const isDuplicateUsername = error.message.includes('profiles_username_key') || 
                                   error.message.toLowerCase().includes('duplicate') ||
                                   error.message.toLowerCase().includes('username');
      
      toast({
        variant: "destructive",
        title: "Signup Failed",
        description: isDuplicateUsername 
          ? "This username is already taken. Please choose a different one."
          : error.message,
      });
    } else {
      toast({
        title: "Account Created!",
        description: "Welcome to FITLINK! Let's get you started.",
      });
      
      // If session exists (auto-confirm enabled), show onboarding
      if (data.session && data.user) {
        const onboardingCompleted = localStorage.getItem("onboarding_completed");
        if (!onboardingCompleted) {
          setOnboardingUserId(data.user.id);
          setShowOnboarding(true);
        } else {
          navigate("/dashboard");
        }
      }
    }
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = (formData.get("signin-email") as string)?.trim();
    const password = formData.get("signin-password") as string;
    // Validate inputs
    if (!email || email === '' || !password) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: "Missing credentials",
        description: !email ? "Please enter your email" : "Please enter your password",
      });
      return;
    }


    // Check rate limit with server before attempting login (fail-secure)
    try {
      const { data: rateLimitCheck, error: rateLimitError } = await supabase.functions.invoke('check-auth-rate-limit', {
        body: { email }
      });

      if (rateLimitError || !rateLimitCheck?.allowed) {
        setLoading(false);
        toast({
          variant: "destructive",
          title: rateLimitCheck?.locked ? "Account Temporarily Locked" : "Too Many Attempts",
          description: rateLimitCheck?.message || "Please try again later.",
        });
        return;
      }
    } catch (error) {
      // FAIL-SECURE: Block login if rate limit service is unavailable
      console.error("Rate limit check failed:", error);
      setLoading(false);
      toast({
        variant: "destructive",
        title: "Service Temporarily Unavailable",
        description: "Unable to verify rate limits. Please try again in a moment.",
      });
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Record the attempt
    await supabase.functions.invoke('record-auth-attempt', {
      body: { 
        email,
        successful: !error
      }
    });

    setLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Login Failed",
        description: error.message,
      });
    } else {
      navigate("/dashboard");
    }
  };

  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("reset-email") as string;

    try {
      const { data, error } = await supabase.functions.invoke('send-auth-email', {
        body: {
          email,
          type: 'recovery',
          redirectTo: `${window.location.origin}/auth`
        }
      });

      setLoading(false);

      if (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message,
        });
      } else {
        toast({
          title: "Check Your Email!",
          description: "We've sent you a password reset link. Please check your email.",
          duration: 8000,
        });
        setResetPasswordOpen(false);
      }
    } catch (error: any) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to send password reset email",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <img src={fitlinkLogo} alt="FITLINK" className="w-16 h-16 mx-auto mb-4 rounded-2xl" />
          <h1 className="text-4xl font-bold text-primary">
            FITLINK
          </h1>
          <p className="text-muted-foreground mt-2">Stay fit, stay linked.</p>
        </div>

        {isResettingPassword ? (
          <Card className="border-border bg-card/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>Set New Password</CardTitle>
              <CardDescription>Enter your new password below</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Enter your new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Must contain at least 12 characters, including uppercase, lowercase, number, and special character
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Updating..." : "Confirm New Password"}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border bg-card/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>Welcome</CardTitle>
              <CardDescription>Sign in or create an account to get started</CardDescription>
            </CardHeader>
            <CardContent>
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      name="signin-email"
                      type="email"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input
                      id="signin-password"
                      name="signin-password"
                      type="password"
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                  
                  <div className="flex gap-2 mt-2">
                    <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
                      <DialogTrigger asChild>
                        <Button variant="link" className="text-sm text-muted-foreground w-full">
                          Forgot Password?
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Reset Password</DialogTitle>
                          <DialogDescription>
                            Enter your email address and we'll send you a link to reset your password.
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handlePasswordReset} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="reset-email">Email</Label>
                            <Input
                              id="reset-email"
                              name="reset-email"
                              type="email"
                              placeholder="you@example.com"
                              required
                            />
                          </div>
                          <Button type="submit" className="w-full" disabled={loading}>
                            {loading ? "Sending..." : "Send Reset Link"}
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      name="username"
                      type="text"
                      placeholder="your_username"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      name="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      name="signup-password"
                      type="password"
                      placeholder="At least 12 characters"
                      minLength={12}
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Must include uppercase, lowercase, number, and special character
                    </p>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>

                  {/* Privacy Notice - App Store Guideline 5.1.2 Compliance */}
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg border border-border">
                    <div className="flex items-start gap-2">
                      <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-muted-foreground">
                        <p className="mb-1">
                          By creating an account, you agree to our{" "}
                          <a 
                            href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-primary hover:underline font-medium"
                          >
                            Terms of Use
                          </a>
                          {" "}and{" "}
                          <Link to="/privacy" className="text-primary hover:underline font-medium">
                            Privacy Policy
                          </Link>
                        </p>
                        <p className="text-[10px]">
                          We collect email, profile info, and workout data to provide fitness tracking features. Your data is never sold to third parties.
                        </p>
                      </div>
                    </div>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        )}
        
        <div className="text-center mt-4 space-y-2">
          <div className="flex items-center justify-center gap-4">
            <a 
              href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-sm text-muted-foreground hover:text-primary"
            >
              Terms of Use
            </a>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary">
              Privacy Policy
            </Link>
          </div>
          <Link to="/support" className="text-sm text-muted-foreground hover:text-primary block">
            Need help? Contact Support
          </Link>
        </div>
      </div>
      
      {showOnboarding && onboardingUserId && (
        <ComprehensiveOnboarding 
          open={showOnboarding}
          userId={onboardingUserId}
          onComplete={() => {
            setShowOnboarding(false);
          }}
        />
      )}
    </div>
  );
};

export default Auth;
